import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
  CAMPAIGN_STATUS_VALUES,
  CAMPAIGN_VERTICAL_VALUES,
  type CampaignStatus,
  type CampaignVertical,
} from '@content-engine/shared';
import {
  createIdeaDraft,
  deleteCampaign,
  fetchBanners,
  fetchCampaign,
  scrapeTikTok,
  updateCampaign,
  uploadCampaignBanner,
  type ScrapeTikTokResult,
} from '../../lib/queries.ts';

function ScrapeMetric({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: number | string;
  hint?: string;
  highlight?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        padding: '10px 12px',
        background: highlight ? 'rgba(64,224,160,0.08)' : 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 6,
      }}
    >
      <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontFamily: 'var(--mono)',
          marginTop: 2,
          color: highlight ? 'var(--good, #40e0a0)' : undefined,
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4, fontFamily: 'var(--mono)' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function CampaignDetailPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const campaignQuery = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => fetchCampaign(id!),
    enabled: Boolean(id),
  });

  const campaign = campaignQuery.data?.campaign;

  const [name, setName] = useState('');
  const [vertical, setVertical] = useState<CampaignVertical>('casino');
  const [status, setStatus] = useState<CampaignStatus>('paused');
  const [keywords, setKeywords] = useState('');
  const [maxPostsPerDay, setMaxPostsPerDay] = useState(5);
  const [cronSchedule, setCronSchedule] = useState('0 */4 * * *');
  const [captionPromptTemplate, setCaptionTemplate] = useState('');
  const [bannerIds, setBannerIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [scrapeResult, setScrapeResult] = useState<ScrapeTikTokResult | null>(null);
  const [scrapeError, setScrapeError] = useState<string | null>(null);

  const bannersQuery = useQuery({
    queryKey: ['banners', 'active', ''],
    queryFn: () => fetchBanners({ status: 'active' }),
  });
  const activeBanners = bannersQuery.data?.banners ?? [];

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setVertical(campaign.vertical);
      setStatus(campaign.status);
      setKeywords(campaign.keywords.join(', '));
      setMaxPostsPerDay(campaign.maxPostsPerDay);
      setCronSchedule(campaign.cronSchedule);
      setCaptionTemplate(campaign.captionPromptTemplate ?? '');
      setBannerIds(campaign.bannerIds ?? []);
    }
  }, [campaign]);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateCampaign(id!, {
        name,
        vertical,
        status,
        keywords: keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
        maxPostsPerDay,
        cronSchedule,
        captionPromptTemplate: captionPromptTemplate || null,
        bannerIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const bannerMutation = useMutation({
    mutationFn: (file: File) => uploadCampaignBanner(id!, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
    onError: (e: Error) => setError(`Banner upload: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCampaign(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      navigate('/campaigns', { replace: true });
    },
    onError: (e: Error) => setError(e.message),
  });

  const scrapeMutation = useMutation({
    mutationFn: () => scrapeTikTok(id!),
    onMutate: () => {
      setScrapeError(null);
      setScrapeResult(null);
    },
    onSuccess: (data) => {
      setScrapeResult(data);
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
    },
    onError: (e: Error) => setScrapeError(e.message),
  });

  const [ideaModalOpen, setIdeaModalOpen] = useState(false);
  const [ideaTopic, setIdeaTopic] = useState('');
  const [ideaError, setIdeaError] = useState<string | null>(null);

  const ideaMutation = useMutation({
    mutationFn: () => createIdeaDraft(id!, ideaTopic.trim()),
    onMutate: () => setIdeaError(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
      setIdeaModalOpen(false);
      setIdeaTopic('');
      navigate('/queue');
    },
    onError: (e: Error) => setIdeaError(e.message),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    updateMutation.mutate();
  }

  if (campaignQuery.isLoading) {
    return <div style={{ padding: 24 }}>{t('campaigns.detail.loading')}</div>;
  }
  if (campaignQuery.isError || !campaign) {
    return (
      <div style={{ padding: 24, color: 'var(--hot)' }}>
        {t('campaigns.detail.notFound')}{' '}
        <button onClick={() => navigate('/campaigns')}>{t('common.back')}</button>
      </div>
    );
  }

  const locale = i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{name || campaign.name}</h1>
          <div className="sub">
            id: {campaign.id} ·{' '}
            {t('campaigns.detail.updatedAt', { at: new Date(campaign.updatedAt).toLocaleString(locale) })}
          </div>
        </div>
        <div className="filters">
          <button onClick={() => navigate('/campaigns')}>{t('campaigns.detail.backToList')}</button>
          <button
            onClick={() => scrapeMutation.mutate()}
            disabled={scrapeMutation.isPending || campaign.keywords.length === 0}
            title={
              campaign.keywords.length === 0
                ? t('campaigns.detail.scrape.noKeywordsTooltip')
                : t('campaigns.detail.scrape.tooltip')
            }
            className="primary"
          >
            {scrapeMutation.isPending
              ? t('campaigns.detail.scrape.running')
              : t('campaigns.detail.scrape.button')}
          </button>
          <button onClick={() => setIdeaModalOpen(true)} title={t('campaigns.detail.idea.tooltip')}>
            {t('campaigns.detail.idea.button')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('campaigns.detail.deleteConfirm', { name: campaign.name }))) deleteMutation.mutate();
            }}
            style={{ borderColor: 'rgba(255,61,110,0.3)', color: 'var(--hot)' }}
          >
            {t('campaigns.detail.delete')}
          </button>
        </div>
      </div>

      {(scrapeMutation.isPending || scrapeResult || scrapeError) && (
        <div
          className="form-section"
          style={{
            marginBottom: 16,
            borderColor: scrapeError
              ? 'rgba(255,61,110,0.3)'
              : scrapeResult
                ? 'rgba(64,224,160,0.3)'
                : undefined,
          }}
        >
          <h3>{t('campaigns.detail.scrape.panelTitle')}</h3>
          {scrapeMutation.isPending && (
            <div className="help">{t('campaigns.detail.scrape.runningHelp')}</div>
          )}
          {scrapeError && <div style={{ color: 'var(--hot)', fontSize: 12 }}>{scrapeError}</div>}
          {scrapeResult && (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <ScrapeMetric
                  label={t('campaigns.detail.scrape.raw')}
                  value={scrapeResult.raw}
                />
                <ScrapeMetric
                  label={t('campaigns.detail.scrape.cheapKept')}
                  value={scrapeResult.cheapKept}
                  hint={Object.entries(scrapeResult.cheapReasons)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(', ')}
                />
                <ScrapeMetric
                  label={t('campaigns.detail.scrape.claudeKept')}
                  value={scrapeResult.claudeUsed ? scrapeResult.claudeKept : '—'}
                  hint={scrapeResult.claudeUsed ? '' : t('campaigns.detail.scrape.claudeOff')}
                />
                <ScrapeMetric
                  label={t('campaigns.detail.scrape.finalDrafts')}
                  value={scrapeResult.finalDrafts}
                  highlight
                />
              </div>

              {scrapeResult.claudeRationale && scrapeResult.claudeRationale.length > 0 && (
                <details style={{ fontSize: 12, marginBottom: 12 }}>
                  <summary style={{ cursor: 'pointer', opacity: 0.7 }}>
                    {t('campaigns.detail.scrape.rationaleToggle')}
                  </summary>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 20, opacity: 0.8 }}>
                    {scrapeResult.claudeRationale.map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                </details>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => navigate('/queue')}>
                  {t('campaigns.detail.scrape.gotoQueue')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setScrapeResult(null);
                    setScrapeError(null);
                  }}
                >
                  {t('common.close')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div className="form-section">
              <h3>{t('campaigns.detail.section1')}</h3>
              <div className="form-row">
                <label>{t('campaigns.detail.name')}</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="form-row inline">
                <div>
                  <label>{t('campaigns.detail.vertical')}</label>
                  <select
                    value={vertical}
                    onChange={(e) => setVertical(e.target.value as CampaignVertical)}
                  >
                    {CAMPAIGN_VERTICAL_VALUES.map((v) => (
                      <option key={v} value={v}>
                        {t(`campaigns.verticals.${v}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>{t('campaigns.detail.status')}</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as CampaignStatus)}
                  >
                    {CAMPAIGN_STATUS_VALUES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>{t('campaigns.detail.section2')}</h3>
              <div className="form-row">
                <label>{t('campaigns.detail.keywords')}</label>
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="casino, slots, jackpot"
                />
                <div className="help">{t('campaigns.detail.keywordsHelp')}</div>
              </div>
              <div className="form-row">
                <label>{t('campaigns.detail.sourcePlatforms')}</label>
                <div className="tag-pick">
                  <span className="tag active">TikTok ×</span>
                  <span className="tag" style={{ opacity: 0.4 }}>
                    Reddit ({t('common.phase2')})
                  </span>
                  <span className="tag" style={{ opacity: 0.4 }}>
                    Twitter ({t('common.phase2')})
                  </span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>{t('campaigns.detail.section3')}</h3>
              <div className="form-row inline">
                <div>
                  <label>{t('campaigns.detail.videoCutter')}</label>
                  <select disabled>
                    <option>WayinVideo (default)</option>
                  </select>
                </div>
                <div>
                  <label>{t('campaigns.detail.clipLength')}</label>
                  <select disabled>
                    <option>15-30 sec</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <label>{t('campaigns.detail.uniqueness')}</label>
                <div className="tag-pick">
                  <span className="tag active">Auto-subtitles ×</span>
                  <span className="tag" style={{ opacity: 0.4 }}>
                    AI hook ({t('common.phase3')})
                  </span>
                </div>
                <div className="help">{t('campaigns.detail.uniquenessHelp')}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="form-section">
              <h3>{t('campaigns.detail.section4')}</h3>
              <div className="form-row inline">
                <div>
                  <label>{t('campaigns.detail.postsPerDay')}</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={maxPostsPerDay}
                    onChange={(e) => setMaxPostsPerDay(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label>{t('campaigns.detail.cron')}</label>
                  <input
                    type="text"
                    value={cronSchedule}
                    onChange={(e) => setCronSchedule(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row">
                <label>{t('campaigns.detail.publishAccounts')}</label>
                <div className="help">{t('campaigns.detail.publishAccountsHelp')}</div>
              </div>
            </div>

            <div className="form-section">
              <h3>{t('campaigns.detail.section5')}</h3>
              <div className="form-row">
                <label>{t('campaigns.detail.moderationMode')}</label>
                <select disabled>
                  <option>Manual (dashboard approval)</option>
                </select>
                <div className="help">{t('campaigns.detail.moderationHelp')}</div>
              </div>
            </div>

            <div className="form-section">
              <h3>{t('campaigns.detail.section6')}</h3>
              <div className="form-row">
                <label>{t('campaigns.detail.captionTemplate')}</label>
                <textarea
                  rows={5}
                  style={{ fontFamily: 'var(--mono)', fontSize: 12 }}
                  value={captionPromptTemplate}
                  onChange={(e) => setCaptionTemplate(e.target.value)}
                  placeholder={t('campaigns.detail.captionPlaceholder')}
                />
              </div>
              <div className="form-row inline">
                <div>
                  <label>{t('campaigns.detail.llmModel')}</label>
                  <select disabled>
                    <option>Claude Sonnet 4.6</option>
                  </select>
                </div>
                <div>
                  <label>{t('campaigns.detail.tokenBudget')}</label>
                  <input type="text" disabled value={`$8 (${t('common.phase2')})`} />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>{t('campaigns.detail.section7')}</h3>
              <div className="form-row">
                <label>{t('campaigns.detail.bannerFile')}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.mp4,.mov,.webm"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) bannerMutation.mutate(file);
                  }}
                />
                <div className="help">
                  {t('campaigns.detail.bannerCurrent', {
                    key: campaign.bannerR2Key ?? t('campaigns.detail.bannerNone'),
                  })}
                  {bannerMutation.isPending && ` · ${t('campaigns.detail.bannerUploading')}`}
                  {bannerMutation.isSuccess && ` · ${t('campaigns.detail.bannerUploaded')}`}
                </div>
                {!campaign.bannerR2Key && (
                  <div className="help" style={{ color: 'var(--amber)' }}>
                    {t('campaigns.detail.bannerWarning')}
                  </div>
                )}
              </div>
              <div className="form-row">
                <label>{t('campaigns.detail.bannersLabel')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {activeBanners.length === 0 && (
                    <div style={{ color: 'var(--text-3)', fontSize: 12 }}>{t('campaigns.detail.bannersNone')}</div>
                  )}
                  {activeBanners.map((b) => {
                    const checked = bannerIds.includes(b.id);
                    return (
                      <label
                        key={b.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 8px',
                          border: '1px solid var(--line)',
                          borderRadius: 4,
                          cursor: 'pointer',
                          background: checked ? 'var(--neon-soft)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setBannerIds((prev) =>
                              e.target.checked ? [...prev, b.id] : prev.filter((id) => id !== b.id),
                            );
                          }}
                          style={{ marginRight: 4 }}
                        />
                        {b.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 12px',
              background: 'rgba(255,61,110,0.1)',
              border: '1px solid rgba(255,61,110,0.3)',
              borderRadius: 6,
              color: 'var(--hot)',
              fontSize: 12,
              margin: '16px 0',
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}
        >
          <button type="button" onClick={() => navigate('/campaigns')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="primary" disabled={updateMutation.isPending}>
            {updateMutation.isPending
              ? t('campaigns.detail.saving')
              : t('campaigns.detail.save')}
          </button>
        </div>
      </form>

      {ideaModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
          }}
          onClick={() => setIdeaModalOpen(false)}
        >
          <div
            style={{
              width: 560,
              maxWidth: '90vw',
              background: 'var(--bg-card)',
              border: '1px solid var(--line)',
              borderRadius: 12,
              padding: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 18, marginBottom: 6 }}>
              {t('campaigns.detail.idea.modal_title')}
            </h2>
            <div
              style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}
            >
              {t('campaigns.detail.idea.modal_desc')}
            </div>
            <textarea
              value={ideaTopic}
              onChange={(e) => setIdeaTopic(e.target.value)}
              placeholder={t('campaigns.detail.idea.placeholder')}
              rows={5}
              maxLength={800}
              autoFocus
              style={{
                width: '100%',
                fontSize: 14,
                fontFamily: 'inherit',
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid var(--line)',
                background: 'var(--bg-input)',
                color: 'var(--text)',
                resize: 'vertical',
                minHeight: 100,
                marginBottom: 12,
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12, fontFamily: 'var(--mono)' }}>
              {ideaTopic.length} / 800
            </div>
            {ideaError && (
              <div
                style={{
                  padding: '8px 10px',
                  background: 'rgba(255,61,110,0.1)',
                  border: '1px solid rgba(255,61,110,0.3)',
                  borderRadius: 4,
                  fontSize: 12,
                  color: 'var(--hot)',
                  marginBottom: 12,
                }}
              >
                {ideaError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setIdeaModalOpen(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="primary"
                onClick={() => ideaMutation.mutate()}
                disabled={ideaTopic.trim().length < 5 || ideaMutation.isPending}
              >
                {ideaMutation.isPending
                  ? t('campaigns.detail.idea.submitting')
                  : t('campaigns.detail.idea.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
